import { getAppId, buildBrowserWorkspaceResourceHeaders } from '@topolo-io/app-shell';
import { offlineFetch } from '../offline-handler';
import { authClient, tokenStorage } from './auth';
import { buildUserFromAccessToken } from './secure-auth';
import { getRoadmapperApiBaseUrl } from './serviceUrls';
import { ROADMAPPER_WORKSPACE_STORAGE_KEY } from './workspace';

const API_BASE = getRoadmapperApiBaseUrl();
const APP_ID = getAppId();

export type GuestAccessMode = 'view' | 'edit';

export interface GuestInviteCapabilities {
  email: boolean;
  whatsapp: boolean;
}

export interface GuestShareRecord {
  token: string;
  shareUrl?: string;
  expiresAt?: string;
  accessMode: GuestAccessMode;
  allowItemNavigation: boolean;
  createdAt: string;
}

export class RoadmapperApiClientBase {
  protected createAuthenticatedFetch(liveOnly = false) {
    const tokens = tokenStorage.getTokens();
    const authenticatedFetch = authClient.createAuthenticatedFetch(tokens, APP_ID);
    return async (url: string, options?: RequestInit) => {
      const scopedHeaders = { ...options?.headers, ...(await this.getAuthHeaders()) };
      // The auth-client fetch is the live transport (bearer + refresh + retry);
      // offlineFetch wraps it with the IndexedDB read-cache / write-queue.
      // Previously the live hop was a bare fetch and the authenticated path was
      // only reached when the network threw, so online requests skipped the
      // auth-client contract entirely.
      if (liveOnly) {
        return authenticatedFetch(url, { ...options, headers: scopedHeaders, cache: 'no-store' });
      }
      return offlineFetch(
        url,
        { ...options, headers: scopedHeaders },
        (input, init) => authenticatedFetch(input as string, init),
      );
    };
  }

  private async getAuthHeaders(): Promise<Record<string, string>> {
    // Auth is @topolo-io/auth-client's contract: the authenticated fetch below
    // attaches the bearer token and X-App-ID, refreshes proactively near expiry,
    // and refreshes-and-retries once on a 401. Only workspace scoping is ours.
    const tokens = tokenStorage.getTokens();
    const user = tokens?.accessToken ? buildUserFromAccessToken(tokens.accessToken) : null;
    return {
      ...buildBrowserWorkspaceResourceHeaders({
        storageKey: ROADMAPPER_WORKSPACE_STORAGE_KEY,
        user,
      }),
    };
  }

  protected async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      if (response.status === 401) {
        try {
          const errorText = await response.text();
          window.dispatchEvent(new CustomEvent('auth:refresh-needed'));
          throw new Error(`API Error: ${response.status} ${errorText}`);
        } catch (error) {
          if (error instanceof Error) throw error;
          window.dispatchEvent(new CustomEvent('auth:refresh-needed'));
          throw new Error(`API Error: ${response.status} Authentication failed`);
        }
      }

      const error = await response.text();
      throw new Error(`API Error: ${response.status} ${error}`);
    }

    const data: unknown = await response.json();
    if (data && typeof data === 'object' && 'data' in data) {
      return ((data as { data: T }).data || data) as T;
    }
    return data as T;
  }

  async createGuestShare(
    roadmapId: string,
    data: { expiresInHours?: number | null; accessMode?: GuestAccessMode; allowItemNavigation?: boolean },
  ): Promise<{ share: GuestShareRecord; capabilities: GuestInviteCapabilities }> {
    return this.postGuestShare(`/roadmaps/${roadmapId}/share-guest`, data);
  }

  async createProjectGuestShare(
    projectId: string,
    data: { expiresInHours?: number | null; accessMode?: GuestAccessMode; allowItemNavigation?: boolean },
  ): Promise<{ share: GuestShareRecord; capabilities: GuestInviteCapabilities }> {
    return this.postGuestShare(`/projects/${projectId}/share-guest`, data);
  }

  async createItemGuestShare(
    itemId: string,
    data: { expiresInHours?: number | null; accessMode?: GuestAccessMode },
  ): Promise<{ share: GuestShareRecord; capabilities: GuestInviteCapabilities }> {
    return this.postGuestShare(`/items/${itemId}/share-guest`, data);
  }

  private async postGuestShare(
    path: string,
    data: { expiresInHours?: number | null; accessMode?: GuestAccessMode; allowItemNavigation?: boolean },
  ): Promise<{ share: GuestShareRecord; capabilities: GuestInviteCapabilities }> {
    const response = await this.createAuthenticatedFetch()(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return this.handleResponse(response);
  }

  async listGuestShares(roadmapId: string) {
    return this.listGuestSharesAt(`/roadmaps/${roadmapId}/share-guest`);
  }

  async listProjectGuestShares(projectId: string) {
    return this.listGuestSharesAt(`/projects/${projectId}/share-guest`);
  }

  async listItemGuestShares(itemId: string) {
    return this.listGuestSharesAt(`/items/${itemId}/share-guest`);
  }

  private async listGuestSharesAt(path: string): Promise<{
    shares: GuestShareRecord[];
    capabilities: GuestInviteCapabilities;
  }> {
    const response = await this.createAuthenticatedFetch()(`${API_BASE}${path}`);
    return this.handleResponse(response);
  }

  async revokeGuestShare(roadmapId: string, token: string) {
    return this.deleteGuestShare(`/roadmaps/${roadmapId}/share-guest/${token}`);
  }

  async revokeProjectGuestShare(projectId: string, token: string) {
    return this.deleteGuestShare(`/projects/${projectId}/share-guest/${token}`);
  }

  async revokeItemGuestShare(itemId: string, token: string) {
    return this.deleteGuestShare(`/items/${itemId}/share-guest/${token}`);
  }

  private async deleteGuestShare(path: string) {
    const response = await this.createAuthenticatedFetch()(`${API_BASE}${path}`, {
      method: 'DELETE',
    });
    return this.handleResponse(response);
  }

  async inviteRoadmapGuestShareByEmail(roadmapId: string, token: string, recipientEmail: string) {
    return this.inviteGuestShare(`/roadmaps/${roadmapId}/share-guest/${token}/invite-email`, recipientEmail);
  }

  async inviteProjectGuestShareByEmail(projectId: string, token: string, recipientEmail: string) {
    return this.inviteGuestShare(`/projects/${projectId}/share-guest/${token}/invite-email`, recipientEmail);
  }

  async inviteItemGuestShareByEmail(itemId: string, token: string, recipientEmail: string) {
    return this.inviteGuestShare(`/items/${itemId}/share-guest/${token}/invite-email`, recipientEmail);
  }

  private async inviteGuestShare(path: string, recipientEmail: string) {
    const response = await this.createAuthenticatedFetch()(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ recipientEmail }),
    });
    return this.handleResponse<{ message: string }>(response);
  }
}
